import { useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useObrasTopHeaderState } from "../../../obras/presentation/components/hooks/obras_top_header_state";
import { AuditoresLista } from "../../domain/models/auditores_lista";
import { useAuditoresFilters } from "../hooks/Auditoria_auditores/filter";
import { useAuditoresPagination } from "../hooks/Auditoria_auditores/pagination";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";

type LoadAuditoresListaFuncState = {
  IdAuditor?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number;
  tipoAuditor?: number;
  pesquisa?: string;
};

export const useAuditoriasAuditoresViewModel = () => {
  const {
    commons: {},
    reports: {},
    auditor: {
      getAllListagemAuditoresUseCase,
      createAuditoresUseCase,
      updateAuditorUseCase,
      deleteAuditoresUseCase,
    },
  } = useDependencyInjection();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [auditoreLista, setAuditoreLista] = useState<AuditoresLista[]>([]);
  const [auditoresModal, setAuditoresModal] = useState<boolean>(false);
  const [auditoresDeleteModal, setAuditoresDeleteModal] =
    useState<boolean>(false);
  const [selectedAuditores, setSelectedAuditores] = useState<
    AuditoresLista | undefined
  >(undefined);
  const navigate = useNavigate();

  const { pushErrorNotification, setCustomLoading } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const obrasTopHeaderState = useObrasTopHeaderState();
  const filters = useAuditoresFilters();
  const pagination = useAuditoresPagination();

  // #region Funções de carregamento

  const loadAuditoresListaState = useCallback(
    async (funcState?: LoadAuditoresListaFuncState) => {
      try {
        const filterID_Auditor = evaluateParamValue(
          funcState?.IdAuditor,
          filters.IdAuditor,
        );

        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage =
          funcState?.rowsPerPage ?? pagination.rowsPerPage;
        const filteredTipoPesquisa =
          funcState?.tipoPesquisa ?? filters.tipoPesquisa;
        const filterTipoAuditor = funcState?.tipoAuditor ?? filters.tipoAuditor;
        const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
        await getAllListagemAuditoresUseCase.getAllListagemAuditores(
          {
            IdAuditor: filterID_Auditor,
            PesquisarPor: filteredTipoPesquisa,
            Pesquisar: filteredPesquisa,
            TipoAuditor: filterTipoAuditor
          },
          filteredPage,
          filteredRowsPerPage,
        );
        setAuditoreLista(getAllListagemAuditoresUseCase.items);
        pagination.setRowsPerPage(getAllListagemAuditoresUseCase.pageSize);
        pagination.setTotalItems(getAllListagemAuditoresUseCase.totalItems);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar auditores!");
      }
    },
    [filters, getAllListagemAuditoresUseCase, pagination],
  );

  //#endregion
  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];

      jobs.push(loadAuditoresListaState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadAuditoresListaState());

      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadAuditoresListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos

  const handleDeleteAuditoresModalOpenState = useCallback(
    (IdAuditor: number | null, openState: boolean) => {
      if (openState) {
        if (IdAuditor && auditoreLista) {
          setSelectedAuditores(
            auditoreLista.find((x) => x.IdAuditor === IdAuditor),
          );
        } else {
          setSelectedAuditores(undefined);
        }
      }
      setAuditoresDeleteModal(openState);
    },
    [auditoreLista],
  );

  const handleDeleteConfirmaAuditores = useCallback(async () => {
    if (selectedAuditores) {
      try {
        setSaving(true);
        await deleteAuditoresUseCase.deleteAuditores(
          selectedAuditores.IdAuditor,
        );
        await loadAuditoresListaState();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(
            e,
            "Não é possível excluir este auditor pois ele está vinculado a uma ou mais Auditorias.",
          ),
        );
      } finally {
        setSaving(false);
        setAuditoresDeleteModal(false);
      }
    }
  }, [
    selectedAuditores,
    deleteAuditoresUseCase,
    loadAuditoresListaState,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        pagination.setPage(page + 1);
        await loadAuditoresListaState({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadAuditoresListaState, pagination, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);

        await loadAuditoresListaState({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadAuditoresListaState, pagination, pushErrorNotification],
  );

  //#endregion

  //#region Filtros
  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filters.clearFilters();
      pagination.setPage(1);
      await loadAuditoresListaState();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar contas a receber!"),
      );
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, loadAuditoresListaState, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string) => {
      useAuditoresFilters.getState().setPesquisa(newValue);
      return async () => {
        try {
          setLoading(true);
          useAuditoresPagination.getState().setPage(1);
          await loadAuditoresListaState({ pesquisa: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar auditore!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadAuditoresListaState, pushErrorNotification],
  );

  const handleTipoAuditorChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useAuditoresFilters.getState().setTipoAuditor(newValue);
      return async () => {
        try {
          setLoading(true);
          useAuditoresPagination.getState().setPage(1);
          await loadAuditoresListaState({ tipoAuditor: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar listagem!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadAuditoresListaState, pushErrorNotification],
  );

  const handleTipoPesquisaChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useAuditoresFilters.getState().setTipoPesquisa(newValue);
      return async () => {
        try {
          setLoading(true);
          useAuditoresPagination.getState().setPage(1);
          await loadAuditoresListaState({ tipoPesquisa: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar auditores!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadAuditoresListaState, pushErrorNotification],
  );

  //#endregion

  const handleCadastroOpenState = useCallback(
    (id: number | null) => {
      if (id) navigate(`/Auditores_cadastro/${id}`);
      else navigate(`/Auditores_cadastro/`);
    },
    [navigate],
  );
  return {
    loading,
    saving,
    errorMessage,
    obrasTopHeaderState,
    auditoreLista,
    auditoresModal,
    selectedAuditores,
    filters,
    pagination,
    auditoresDeleteModal,
    handleCadastroOpenState,
    handleReload,
    handleDeleteConfirmaAuditores,
    handleDeleteAuditoresModalOpenState,
    handleClearFilters,
    handlePesquisaChange,
    handleTipoPesquisaChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleTipoAuditorChange,
  };
};
